<script setup lang="ts">
    import type { ReferenceElement } from 'reka-ui';
    import { computedWithControl } from '@vueuse/core';
    import {
        ComboboxAnchor,
        ComboboxContent,
        ComboboxInput,
        ComboboxItem,
        ComboboxPortal,
        ComboboxRoot,
        useFilter,
    } from 'reka-ui';
    import { computed, ref, watch, nextTick } from 'vue';
    import {
        getAnchorRect,
        getSearchValue,
        getTrigger,
        getTriggerOffset,
        replaceValue,
    } from '@/utils/comments';
    import { ArrowUp, LoaderCircle } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import { useKeepersEmployeeData } from '@/composables/useKeepersEmployeeData';
    import type { User } from '@/types';
    import axios from 'axios';
    import { useMutation, useQueryClient } from '@tanstack/vue-query';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import QueryKey from '@/enums/QueryKey';

    const { contains } = useFilter({ sensitivity: 'base' });

    const queryClient = useQueryClient();

    type MentionedUser = {
        listValue: string;
        value: string;
        email: string;
    };

    const props = defineProps<{
        companyId: number;
        statusId: number;
    }>();

    const { keepersEmployees } = useKeepersEmployeeData(props.companyId);

    const page = usePage<SharedData>();

    const currentUser = computed(() => page.props.auth.user);

    const users = computed(() =>
        keepersEmployees.value.map((user: User) => {
            return {
                listValue: user.name,
                value: `@${user.name}`,
                email: user.email,
            };
        }),
    );

    const value = ref('');
    const mentions = ref<string[]>([]);
    const isMentioning = ref(false);
    const searchValue = ref('');
    const open = ref(false);
    const textareaRef = ref<InstanceType<typeof ComboboxInput>>();

    const reference = computedWithControl(
        () => [searchValue.value, open.value],
        () =>
            ({
                getBoundingClientRect: () => {
                    if (textareaRef.value?.$el) {
                        const { x, y, height } = getAnchorRect(textareaRef.value?.$el);
                        return { x, y, height, top: y, left: x, width: 0 };
                    } else {
                        return null;
                    }
                },
            }) as ReferenceElement,
    );

    const filteredUsers = computed(() => {
        if (!isMentioning.value) return [];
        return users.value
            .filter(
                (user: MentionedUser) =>
                    contains(user.listValue, searchValue.value) &&
                    user.email !== currentUser.value.email &&
                    !mentions.value.includes(user.email),
            )
            .map((user: MentionedUser) => user.listValue);
    });

    watch(
        () => filteredUsers.value.length,
        () => {
            open.value = !!filteredUsers.value.length;
        },
    );

    function autoResizeTextarea() {
        const textarea = textareaRef.value?.$el as HTMLTextAreaElement | undefined;
        if (!textarea) return;

        textarea.style.height = 'auto';
        const newHeight = Math.min(textarea.scrollHeight, 192);
        textarea.style.height = `${newHeight}px`;
    }

    function syncMentionsWithText() {
        const mentionRegex = /@(\w+)(?:\s|$|[^\w])/g;
        const textMentions: string[] = [];
        const matches = Array.from(value.value.matchAll(mentionRegex));

        for (const match of matches) {
            const mentionName = match[1];
            const user = users.value.find((u: MentionedUser) => u.listValue === mentionName);
            if (user) {
                textMentions.push(user.email);
            }
        }

        mentions.value = textMentions;
    }

    function handleChange(ev: InputEvent) {
        const target = ev.target as HTMLTextAreaElement;
        const trigger = getTrigger(target);
        const search = getSearchValue(target);

        if (trigger === '@') {
            isMentioning.value = true;
            open.value = true;
        } else if (!search) {
            isMentioning.value = false;
            open.value = false;
        }

        value.value = target.value;
        searchValue.value = search;

        if (!isMentioning.value) open.value = false;

        // Sync mentions whenever text changes
        syncMentionsWithText();

        nextTick(() => {
            autoResizeTextarea();
        });
    }

    function handleSelect(ev: CustomEvent) {
        const textarea = textareaRef.value?.$el;
        if (!textarea) return;

        const offset = getTriggerOffset(textarea);
        const selectedUser = users.value.find(
            (user: MentionedUser) => user.listValue === ev.detail.value,
        );
        if (!selectedUser) return;

        ev.preventDefault();

        isMentioning.value = false;
        value.value = replaceValue(value.value, offset, searchValue.value, selectedUser.value);

        // Sync mentions after adding a new one
        syncMentionsWithText();

        const nextCaretOffset = offset + selectedUser.value.length + 1;

        // Set cursor position
        nextTick(() => {
            textarea.setSelectionRange(nextCaretOffset, nextCaretOffset);
            autoResizeTextarea();
        });
    }

    watch(value, () => {
        nextTick(() => {
            autoResizeTextarea();
        });
    });

    const emit = defineEmits<{
        (e: 'comment-added'): void;
    }>();

    const { isPending, isError, error, isSuccess, mutate } = useMutation({
        mutationFn: (newComment: { body: string; mentions: string[]; company_id: number }) =>
            axios.post(route('v2.dashboard.comments.store', props.statusId), newComment),
        onSuccess: (data) => {
            queryClient.setQueryData(
                [QueryKey.COMPANY_COMMENTS, props.companyId],
                (oldData: Comment[]) => (oldData ? [...oldData, data.data] : oldData),
            );
            value.value = '';
            mentions.value = [];
            emit('comment-added');
        },
    });

    function addComment() {
        if (isPending.value) return;
        mutate({
            body: value.value,
            mentions: mentions.value,
            company_id: props.companyId,
        });
    }
</script>

<template>
    <ComboboxRoot
        v-model:open="open"
        ignore-filter
        :reset-search-term-on-blur="false"
        class="text-foreground flex flex-col"
    >
        <div class="relative">
            <ComboboxInput
                id="comment"
                ref="textareaRef"
                v-model="value"
                as="textarea"
                class="border-input z-50 flex min-h-10 w-full min-w-0 resize-none overflow-x-hidden overflow-y-hidden rounded-md border bg-transparent py-2.5 pr-10 pl-2.5 text-xs shadow-xs transition-[color,box-shadow] outline-none"
                rows="1"
                :placeholder="$t('dashboards.comment.placeholder')"
                @input="handleChange"
                @pointerdown="open = false"
                @keydown.enter="
                    (ev: KeyboardEvent) => {
                        if (open) ev.preventDefault();
                    }
                "
                @keydown.left.right="open = false"
            />
            <Button
                variant="secondary"
                size="icon"
                class="absolute top-2.5 right-2.5 size-5"
                @click="addComment"
                :disabled="!value || isPending"
            >
                <ArrowUp
                    v-if="!isPending"
                    class="text-white"
                />
                <LoaderCircle
                    v-else
                    class="size-3 animate-spin text-white"
                />
            </Button>
        </div>
        <ComboboxAnchor :reference="reference" />

        <ComboboxPortal>
            <ComboboxContent
                v-if="filteredUsers.length"
                position="popper"
                side="bottom"
                align="start"
                class="z-50 max-h-48 max-w-80 overflow-x-hidden overflow-y-auto rounded-md border bg-white p-1.5 shadow-md"
            >
                <ComboboxItem
                    v-for="user in filteredUsers"
                    :key="user"
                    :value="user"
                    class="data-[highlighted]:bg-primary/10 data-[highlighted]:text-primary flex cursor-default rounded-sm px-2 py-1 text-xs"
                    @select="handleSelect"
                >
                    <span class="truncate">{{ user }}</span>
                </ComboboxItem>
            </ComboboxContent>
        </ComboboxPortal>
    </ComboboxRoot>
</template>
